import { useState, useCallback } from 'react'
import type { OrgType } from '@/lib/public-trpc'
import { getUtmContentSlug, utmSlugToFormOrgType } from '@/lib/utm-utils'
import { trackFormPrefill } from '@/lib/analytics'

// Re-export types consumers already import from here
export type { OrgType }

// -----------------------------------------------------------------------------
// New service + budget model (2026-04-12 redesign)
// -----------------------------------------------------------------------------
// The form now collects a multi-select of services and a monthly-spend range,
// replacing the old single-select `needs` and MRR-tier `budget`. The old fields
// still exist on the backend Zod schema, so at submission time we MAP the new
// shape onto the old fields (see mapServicesToNeeds / mapBudgetRangeToBudget
// in IntakeWizard.tsx). This keeps the frontend redesign decoupled from the
// app server deploy pipeline.

export type ServiceId =
  | 'cms-platform' // pre-checked + locked — $20/mo base floor
  | 'new-website-content-ready'
  | 'new-website-with-coaching'
  | 'branding-logo'
  | 'custom-design'
  | 'ongoing-updates'
  | 'email-marketing'
  | 'social-media'

export type BudgetRange = '20-50' | '50-150' | '150-300' | '300-600' | '600-plus' | 'unsure'

export interface IntakeFormData {
  name: string
  email: string
  organization: string
  website: string
  orgType: OrgType | undefined
  services: ServiceId[]
  budgetRange: BudgetRange | undefined
  subscribeNewsletter: boolean
}

export interface SubmissionResult {
  calendlyUrl?: string
  selfServeUrl?: string
}

// -----------------------------------------------------------------------------
// 5-step flow (down from 9)
// -----------------------------------------------------------------------------
export type IntakeStep = 'contact' | 'organization' | 'services' | 'budget' | 'schedule'

const STEPS: IntakeStep[] = ['contact', 'organization', 'services', 'budget', 'schedule']

export function useIntakeWizard() {
  const [step, setStep] = useState<IntakeStep>('contact')
  const [direction, setDirection] = useState(1)
  const [formData, setFormData] = useState<IntakeFormData>(() => {
    // Pre-fill orgType from UTM params when arriving from outreach emails.
    // The welcome page forwards utm_content={orgSlug} to /get-started.
    const slug = getUtmContentSlug()
    const prefilledOrgType = slug ? utmSlugToFormOrgType(slug) : undefined
    if (prefilledOrgType && slug) {
      trackFormPrefill(slug)
    }
    return {
      name: '',
      email: '',
      organization: '',
      website: '',
      orgType: prefilledOrgType,
      // cms-platform is the $20/mo floor price — always selected, cannot be unchecked
      services: ['cms-platform'],
      budgetRange: undefined,
      subscribeNewsletter: true,
    }
  })
  const [submissionResult, setSubmissionResult] = useState<SubmissionResult | null>(null)

  const currentStepIndex = STEPS.indexOf(step)
  const totalSteps = STEPS.length
  const canGoBack = currentStepIndex > 0

  const updateField = useCallback(<K extends keyof IntakeFormData>(field: K, value: IntakeFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const goNext = useCallback(() => {
    const nextIndex = currentStepIndex + 1
    if (nextIndex < STEPS.length) {
      setDirection(1)
      setStep(STEPS[nextIndex])
    }
  }, [currentStepIndex])

  const goBack = useCallback(() => {
    const prevIndex = currentStepIndex - 1
    if (prevIndex >= 0) {
      setDirection(-1)
      setStep(STEPS[prevIndex])
    }
  }, [currentStepIndex])

  const goToStep = useCallback(
    (targetStep: IntakeStep) => {
      const targetIndex = STEPS.indexOf(targetStep)
      setDirection(targetIndex > currentStepIndex ? 1 : -1)
      setStep(targetStep)
    },
    [currentStepIndex]
  )

  const completeSubmission = useCallback((result: SubmissionResult) => {
    setSubmissionResult(result)
    setDirection(1)
    setStep('schedule')
  }, [])

  return {
    step,
    direction,
    formData,
    updateField,
    goNext,
    goBack,
    goToStep,
    canGoBack,
    currentStep: currentStepIndex + 1,
    totalSteps,
    submissionResult,
    completeSubmission,
  }
}
